"use client";
import { Button } from "@/components/atoms/button";
import { NavLink } from "../footer/BottomFooter";
import {
  NavigationDatasourcesQuery,
  GotoPassInfoLabelQuery,
} from "@/types/generated";
import {
  BOTTOM_NAVIGATION_ITEM_NAME,
  BOTTOM_NAVIGATION,
  LOGIN_BUTTON_ITEM_NAME,
  LOGIN_BUTTON,
} from "@/utils";
import { useEffect, useState } from "react";
import UserInfoModal from "../footer/UserInfoModal";
import { IUserInfo } from "../footer/GlobalFooter";
import {
  generateLinkFromGeneralLink,
  generateTargetFromLink,
  LinkTarget,
} from "@/lib/utils";
import { useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import { CAPILLARY_API_URL } from "@/configs/config";
import { getPublicEnv } from "@/utils/env";

interface ButtonLoginProps {
  navigationDatasources?: Omit<NavigationDatasourcesQuery, "__typename">;
  userInfo?: GotoPassInfoLabelQuery;
  session?: string;
  renderLogout?: boolean;
  enableAuth?: boolean;
  design?: string;
}

const ButtonLogin = ({ navigationDatasources, userInfo, enableAuth, design }: ButtonLoginProps) => {
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const [session, setSession] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [point, setPoint] = useState<any>(null);


  useEffect(() => {
    const fetchSession = async () => {
      try {
        const sessionRes = await fetch('/api/getAccessToken', {
          cache: "no-store",
        });
        if (!sessionRes.ok) {
          throw new Error('Failed to fetch session');
        }
        const sessionData = await sessionRes.json();
        setSession(sessionData?.accessToken)
      } catch (error) {
        console.log('Error fetching session:', error);
      }
    };

    fetchSession();
  }, []);

  useEffect(() => {
    if (!session) return;

    const fetchCapillaryDirect = async () => {
      try {
        const res = await fetch(CAPILLARY_API_URL, {
          cache: "no-store",
          headers: {
            Authorization: `Bearer ${session}`,
            CAP_BRAND: getPublicEnv('NEXT_PUBLIC_CAP_BRAND') || "FORTRESSUAT",
          },
        });

        if (!res.ok) throw new Error(`Capillary error ${res.status}`);
        const json = await res.json();

        if (json.profiles) {
          const name =
            ` ${json.profiles[0]?.lastName ?? ""} ${json.profiles[0]?.firstName ?? ""}`.trim();
          const dataUpdate: IUserInfo = {
            name,
            point: json.pointsSummary?.loyaltyPoints ?? 0,
            rank: json.pointsSummary?.currentSlab ?? "",
            status: json.statusLabel ?? "",
          };
          setPoint(dataUpdate);
          localStorage.setItem("goto-user-info", JSON.stringify(dataUpdate));
        }
      } catch (err) {
        if (!(err instanceof DOMException && err.name === "AbortError")) {
          console.log("Fetch Capillary API failed:", err);
        }
      }
    };

    fetchCapillaryDirect();
  }, [session]);

  const bottomNavigation =
    navigationDatasources?.mystay?.shared_datasource?.navigation_datasources.find(
      (item) =>
        item?.item_name === BOTTOM_NAVIGATION_ITEM_NAME ||
        item?.documentId === BOTTOM_NAVIGATION,
    );
  const actions: NavLink[] = bottomNavigation?.menu_items
    ? bottomNavigation.menu_items
      ?.map((item) => {
        if (session) {
          return {
            itemName: item?.item_name,
            documentId: item?.documentId,
            title: item?.title_after_loggin ?? item?.menu_title ?? "",
            link:
              generateLinkFromGeneralLink(
                item?.button_link_after_loggin || item?.menu_link,
                locale,
              ) || "#",
            target: generateTargetFromLink(
              item?.button_link_after_loggin || item?.menu_link,
            ),
            show: item?.show,
            defaultLink: generateLinkFromGeneralLink(
              item?.button_link_after_loggin,
              locale,
            ) || "#",
          };
        } else {
          return {
            itemName: item?.item_name,
            documentId: item?.documentId,
            title: item?.menu_title,
            show: item?.show,
            link: generateLinkFromGeneralLink(item?.menu_link, locale) || "#",
            target: generateTargetFromLink(item?.menu_link),
            defaultLink: generateLinkFromGeneralLink(
              item?.button_link_after_loggin,
              locale,
            ) || "#",
          };
        }
      })
      .filter((item) => !!item.title && !!item.documentId)
    : [];
  const actionLogin = actions?.find(
    (a) =>
      a.itemName === LOGIN_BUTTON_ITEM_NAME || a.documentId === LOGIN_BUTTON,
  );

  if (!actionLogin?.show) {
    return null;
  }

  const isLinkValid = actionLogin?.link && actionLogin.link !== "#";

  const isCRIDesign = "cri" === design;
  const isArtDesign = "art" === design;

  return (
    <>
      {!session && actionLogin?.show ? (
        isLinkValid && (
          <a
            className={`btn-registration items-center justify-center gap-2 whitespace-nowrap disabled:pointer-events-none outline-none cursor-pointer transition-colors border text-sm px-4 rounded-md bg-[var(--fill-base-white)] border-[var(--stroke-base-primary)]  hover:bg-[var(--fill-primary-tonal-lighter)] active:bg-[var(--fill-primary-tonal-normal)] disabled:border-[var(--stroke-gray-light)] py-[18px] lg:flex truncate overflow-hidden  text-ellipsis md:text-sm ${isCRIDesign ? "h-8 border-none rounded-none text-[#1F1F1F] font-[700] text-sm flex w-full" : isArtDesign ? "h-8 text-[#1F1F1F] flex text-xs hover:bg-white active:bg-white" : "h-[46px] text-[var(--text-base-primary)] hidden text-xs"}`}
            target={actionLogin?.target as LinkTarget}
            href={enableAuth ? `${actionLogin?.link}?inAppRedirect=${pathname}` : `${actionLogin?.defaultLink}`}
          >
            {actionLogin?.title}
          </a>
        )
      ) : (
        <>
          <UserInfoModal
            userInfo={userInfo}
            isOpen={open}
            setIsOpen={setOpen}
            point={point}
          />
          <div className="flex gap-2">
            <Button
              data-wovn-ignore
              className={`py-[18px] lg:flex ${isCRIDesign ? "border-none rounded-none text-[#1F1F1F] font-[700] flex w-full text-sm" : isArtDesign ? "flex text-xs" : "hidden h-[46px]"}`}
              hierarchy="outline"
              onClick={() => {
                setOpen(true);
              }}
            >
              <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 17.5C1 14.5 5 14.5 7 12.5C8 11.5 5 11.5 5 6.5C5 3.167 6.333 1.5 9 1.5C11.667 1.5 13 3.167 13 6.5C13 11.5 10 11.5 11 12.5C13 14.5 17 14.5 17 17.5" stroke="#16225E" strokeWidth="2" strokeLinecap="square" />
              </svg>
              {point?.name}
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default ButtonLogin;